<template>
  <div class="acuity-level-edit">
    <div class="container my-4">
      <!-- Header with actions -->
      <div class="header-section">
        <h1>Edit Acuity Level</h1>
        <div class="action-buttons">
          <button
            class="btn btn-secondary"
            @click="$router.push({ name: 'PatientActivityLevelList' })"
          >
            <i class="fas fa-arrow-left"></i> Back to List
          </button>
        </div>
      </div>

      <!-- Loading Indicator -->
      <div v-if="loading" class="loading-container">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Error Alert -->
      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>

      <!-- Form -->
      <div v-if="!loading" class="card">
        <div class="card-body">
          <form @submit.prevent="updateAcuityLevel">
            <div class="row mb-3">
              <div class="col-md-6">
                <div class="form-group mb-3">
                  <label for="name" class="form-label">Name *</label>
                  <input
                    type="text"
                    id="name"
                    v-model="acuityLevel.name"
                    class="form-control"
                    :class="{ 'is-invalid': errors.name }"
                    placeholder="Enter acuity level name"
                    required
                  />
                  <div v-if="errors.name" class="invalid-feedback">
                    {{ errors.name }}
                  </div>
                </div>
              </div>
              
              <div class="col-md-6">
                <div class="form-group mb-3">
                  <label for="code" class="form-label">Code *</label>
                  <input
                    type="text"
                    id="code"
                    v-model="acuityLevel.code"
                    class="form-control"
                    :class="{ 'is-invalid': errors.code }"
                    placeholder="Enter a unique code (e.g., ACU1)"
                    required
                  />
                  <div v-if="errors.code" class="invalid-feedback">
                    {{ errors.code }}
                  </div>
                </div>
              </div>
            </div>
            
            <div class="row mb-3">
              <div class="col-md-6">
                <div class="form-group mb-3">
                  <label for="level" class="form-label">Level *</label>
                  <select
                    id="level"
                    v-model.number="acuityLevel.level"
                    class="form-select"
                    :class="{ 'is-invalid': errors.level }"
                    required
                  >
                    <option value="" disabled>Select level</option>
                    <option value="1">Level 1 (Minimal Care)</option>
                    <option value="2">Level 2 (Below Average Care)</option>
                    <option value="3">Level 3 (Average Care)</option>
                    <option value="4">Level 4 (Above Average Care)</option>
                    <option value="5">Level 5 (Intensive Care)</option>
                  </select>
                  <div v-if="errors.level" class="invalid-feedback">
                    {{ errors.level }}
                  </div>
                </div>
              </div>
              
              <div class="col-md-6">
                <div class="form-group mb-3">
                  <label for="nurse_patient_ratio" class="form-label">Nurse-Patient Ratio *</label>
                  <div class="input-group">
                    <span class="input-group-text">1:</span>
                    <input
                      type="number"
                      id="nurse_patient_ratio"
                      v-model.number="acuityLevel.nurse_patient_ratio"
                      class="form-control"
                      :class="{ 'is-invalid': errors.nurse_patient_ratio }"
                      min="1"
                      max="20"
                      required
                    />
                    <div v-if="errors.nurse_patient_ratio" class="invalid-feedback">
                      {{ errors.nurse_patient_ratio }}
                    </div>
                  </div>
                  <small class="form-text text-muted">Number of patients per nurse</small>
                </div>
              </div>
            </div>
            
            <div class="form-group mb-4">
              <label for="description" class="form-label">Description</label>
              <textarea
                id="description"
                v-model="acuityLevel.description"
                class="form-control"
                :class="{ 'is-invalid': errors.description }"
                rows="4"
                placeholder="Enter description of this acuity level"
              ></textarea>
              <div v-if="errors.description" class="invalid-feedback">
                {{ errors.description }}
              </div>
            </div>
            
            <div class="d-flex justify-content-end">
              <button
                type="button"
                class="btn btn-secondary me-2"
                @click="$router.push({ name: 'PatientActivityLevelList' })"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="saving"
              >
                <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                {{ saving ? 'Saving...' : 'Update Acuity Level' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PatientActivityLevelEdit',
  data() {
    return {
      acuityLevel: {
        name: '',
        code: '',
        level: '',
        nurse_patient_ratio: 4,
        description: ''
      },
      errors: {},
      error: null,
      loading: false,
      saving: false
    }
  },
  methods: {
    validateForm() {
      this.errors = {};
      let isValid = true;
      
      if (!this.acuityLevel.name.trim()) {
        this.errors.name = 'Name is required';
        isValid = false;
      }
      
      if (!this.acuityLevel.code.trim()) {
        this.errors.code = 'Code is required';
        isValid = false;
      } else if (!/^[A-Za-z0-9_-]+$/.test(this.acuityLevel.code)) {
        this.errors.code = 'Code must contain only letters, numbers, underscores and hyphens';
        isValid = false;
      }
      
      if (!this.acuityLevel.level) {
        this.errors.level = 'Level is required';
        isValid = false;
      }
      
      if (!this.acuityLevel.nurse_patient_ratio) {
        this.errors.nurse_patient_ratio = 'Nurse-patient ratio is required';
        isValid = false;
      } else if (this.acuityLevel.nurse_patient_ratio < 1) {
        this.errors.nurse_patient_ratio = 'Ratio must be at least 1';
        isValid = false;
      } else if (this.acuityLevel.nurse_patient_ratio > 20) {
        this.errors.nurse_patient_ratio = 'Ratio cannot exceed 20';
        isValid = false;
      }
      
      return isValid;
    },
    
    async fetchAcuityLevel() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await this.$api.get(`/patient-activity-levels/${this.$route.params.id}`);
        this.acuityLevel = response.data.data;
      } catch (error) {
        console.error('Error fetching acuity level:', error);
        this.error = error.response?.data?.message || 'Failed to load acuity level data. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    
    async updateAcuityLevel() {
      if (!this.validateForm()) {
        return;
      }
      
      this.saving = true;
      this.error = null;
      
      try {
        await this.$api.put(`/patient-activity-levels/${this.$route.params.id}`, this.acuityLevel);
        this.$toast.success('Acuity level updated successfully');
        this.$router.push({name: 'PatientActivityLevelList'});
      } catch (error) {
        console.error('Error updating acuity level:', error);
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.error = error.response?.data?.message || 'Failed to update acuity level. Please try again.';
        }
      } finally {
        this.saving = false;
      }
    }
  },
  mounted() {
    this.fetchAcuityLevel();
  }
}
</script>

<style scoped>
.acuity-level-edit {
  padding: 20px;
  background-color: white;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 50px 0;
}

.card {
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  border: none;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
}

@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .action-buttons {
    width: 100%;
  }
}
</style>
